import { api } from "./api";

export interface PaymentResponse {
  success: boolean;
  message: string;
  data: {
    paymentId: string;
    transactionId: string;
    status: string;
    amount: number;
    currency: string;
    paymentLink?: string;
  };
}

export interface PaymentStatusResponse {
  success: boolean;
  data: {
    status: string;
    transactionId: string;
    course?: string;
    mentorshipSession?: string;
    paymentId?: string;
    enrollmentCreated?: boolean;
  };
}

export interface PaymentHistoryResponse {
  success: boolean;
  count: number;
  data: Payment[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface MentorshipPaymentResponse {
  success: boolean;
  message: string;
  data: {
    paymentId: string;
    transactionId: string;
    paymentLink: string;
    amount: number;
    currency: string;
    sessionId: string;
  };
}

export interface Payment {
  _id: string;
  user: {
    _id: string;
    name: string;
    email: string;
  };
  course?: {
    _id: string;
    title: string;
    thumbnail?: string;
  };
  mentorshipSession?: {
    _id: string;
    topic: string;
    scheduledAt: string;
    status: string;
  };
  amount: number;
  currency: string;
  status: "pending" | "completed" | "failed" | "cancelled" | "refunded";
  transactionId: string;
  externalId: string;
  createdAt: string;
  completedAt?: string;
  metadata?: {
    courseTitle?: string;
    courseId?: string;
    sessionTopic?: string;
    sessionId?: string;
  };
}

// ✅ New Transaction interface for unified transactions
export interface Transaction {
  _id: string;
  user: {
    _id: string;
    name: string;
    email: string;
    avatar?: string;
  };
  course?: {
    _id: string;
    title: string;
    thumbnail?: string;
  };
  amount: number;
  currency: string;
  paymentMethod: string;
  transactionId: string;
  reference: string;
  status: "pending" | "completed" | "failed" | "refunded";
  type:
    | "course_enrollment"
    | "mentorship_session"
    | "certificate"
    | "subscription";
  description: string;
  metadata?: any;
  createdAt: string;
  updatedAt: string;
}

export const paymentApi = {
  // Course Payment APIs
  /**
   * Create payment for a course
   * @param courseId - Course ID to enroll in
   * @param mobileNumber - Optional mobile number for payment
   * @param discountAmount - Optional discount amount from coupon
   */
  createPayment: (
    courseId: string,
    mobileNumber?: string,
    discountAmount?: number,
  ) => {
    return api.post<PaymentResponse>("/payments", {
      courseId,
      mobileNumber,
      discountAmount,
    });
  },

  /**
   * Check payment status for course (polling endpoint)
   * @param transactionId - Transaction ID from payment
   */
  checkPaymentStatus: (transactionId: string) => {
    return api.get<PaymentStatusResponse>(`/payments/status/${transactionId}`);
  },

  /**
   * Verify payment for course (fallback verification)
   * @param transactionId - Transaction ID from payment
   */
  verifyPayment: (transactionId: string) => {
    return api.get<PaymentStatusResponse>(`/payments/verify/${transactionId}`);
  },

  /**
   * Get user's course payment history
   * @param options - Pagination options
   */
  getMyPayments: (options?: { page?: number; limit?: number }) => {
    const params = new URLSearchParams();
    if (options?.page) params.append("page", String(options.page));
    if (options?.limit) params.append("limit", String(options.limit));
    const queryString = params.toString();
    return api.get<PaymentHistoryResponse>(
      `/payments/my-payments${queryString ? `?${queryString}` : ""}`,
    );
  },

  // Mentorship Payment APIs
  /**
   * Create payment for a mentorship session
   * @param sessionId - Mentorship session ID
   */
  createMentorshipPayment: (sessionId: string) => {
    return api.post<MentorshipPaymentResponse>("/payments/mentorship", {
      sessionId,
    });
  },

  /**
   * Check mentorship payment status
   * @param transactionId - Transaction ID from payment
   */
  checkMentorshipPaymentStatus: (transactionId: string) => {
    return api.get<PaymentStatusResponse>(
      `/payments/status/mentorship/${transactionId}`,
    );
  },

  /**
   * Verify mentorship payment
   * @param transactionId - Transaction ID from payment
   */
  verifyMentorshipPayment: (transactionId: string) => {
    return api.get<PaymentStatusResponse>(
      `/payments/verify/mentorship/${transactionId}`,
    );
  },

  /**
   * Get user's mentorship payment history
   * @param options - Pagination options
   */
  getMyMentorshipPayments: (options?: { page?: number; limit?: number }) => {
    const params = new URLSearchParams();
    if (options?.page) params.append("page", String(options.page));
    if (options?.limit) params.append("limit", String(options.limit));
    const queryString = params.toString();
    return api.get<PaymentHistoryResponse>(
      `/payments/my-mentorship-payments${queryString ? `?${queryString}` : ""}`,
    );
  },

  // ✅ NEW: Get all transactions (unified view)
  getMyTransactions: (params?: {
    page?: number;
    limit?: number;
    status?: string;
    type?: string;
    startDate?: string;
    endDate?: string;
  }) => {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append("page", String(params.page));
    if (params?.limit) queryParams.append("limit", String(params.limit));
    if (params?.status) queryParams.append("status", params.status);
    if (params?.type) queryParams.append("type", params.type);
    if (params?.startDate) queryParams.append("startDate", params.startDate);
    if (params?.endDate) queryParams.append("endDate", params.endDate);

    return api.get<{
      success: boolean;
      data: Transaction[];
      pagination: {
        page: number;
        limit: number;
        total: number;
        pages: number;
      };
    }>(`/payments/transactions?${queryParams.toString()}`);
  },

  // ✅ NEW: Get single transaction by ID
  getTransactionById: (transactionId: string) => {
    return api.get<{ success: boolean; data: Transaction }>(
      `/payments/transactions/${transactionId}`,
    );
  },

  // Admin APIs
  /**
   * Get all payments (admin only)
   * @param options - Pagination and filter options
   */
  getAllPayments: (options?: {
    page?: number;
    limit?: number;
    status?: "pending" | "completed" | "failed" | "cancelled" | "refunded";
  }) => {
    const params = new URLSearchParams();
    if (options?.page) params.append("page", String(options.page));
    if (options?.limit) params.append("limit", String(options.limit));
    if (options?.status) params.append("status", options.status);
    const queryString = params.toString();
    return api.get<PaymentHistoryResponse & { totalRevenue: number }>(
      `/payments/all${queryString ? `?${queryString}` : ""}`,
    );
  },
};

export default paymentApi;
